import { PureComponent } from 'react'
import { useSelector } from 'react-redux'
import { Radio } from 'antd'

import Box from '~/core/Box'
import { getToolVisibility } from '~/redux-modules/qmr-viewer/selectors'

import {
    CONE_INFO,
    CYLINDER_INFO,
    IN,
    MM,
    POINT_TO_POINT_INFO,
    SPHERE_INFO,
    TORUS_INFO,
    TWO_CYLINDERS_INFO,
    TWO_PLANES_INFO,
} from './constants'
import styles from './styles.css'

const toPlaces = decimal => {
    return Number.isFinite(decimal) ? decimal.toFixed(3) : ''
}

const MeasurementField = props => {
    const { name, data, units } = props
    const value = units ? `${toPlaces(data)}${units}` : toPlaces(data)

    return (
        <Box className={styles.dataRow}>
            <Box className={styles.name}>{`${name}: `}</Box>
            <Box className={styles.data}>{value}</Box>
        </Box>
    )
}

class PanelInner extends PureComponent {
    renderPlanes(info) {
        const { units } = this.props.values
        const { distance, angle } = info

        return (
            <>
                {!!distance && (
                    <MeasurementField
                        name="distance"
                        data={distance}
                        units={units}
                    />
                )}
                <MeasurementField name="angle" data={angle} />
            </>
        )
    }

    renderSphere(info) {
        const { units } = this.props.values

        return (
            <>
                <MeasurementField
                    name="diameter"
                    data={info.diameter}
                    units={units}
                />
                <MeasurementField
                    name="radius"
                    data={info.radius}
                    units={units}
                />
            </>
        )
    }

    renderTorus(info) {
        const { units } = this.props.values

        return (
            <>
                <MeasurementField
                    name="diameter"
                    data={info.diameter}
                    units={units}
                />
                <MeasurementField
                    name="radius"
                    data={info.radius}
                    units={units}
                />
            </>
        )
    }

    renderCone(info) {
        const { units } = this.props.values

        return (
            <>
                <MeasurementField
                    name="length"
                    data={info.length}
                    units={units}
                />
                {Number.isFinite(info.angle) && (
                    <MeasurementField name="angle" data={info.angle} />
                )}
            </>
        )
    }

    renderCylinder(info) {
        const { units } = this.props.values

        return (
            <>
                <MeasurementField
                    name="diameter"
                    data={info.diameter}
                    units={units}
                />
                <MeasurementField
                    name="radius"
                    data={info.radius}
                    units={units}
                />
                <MeasurementField
                    name="length"
                    data={info.length}
                    units={units}
                />
                <MeasurementField name="ratio" data={info.ratio} />
            </>
        )
    }

    renderCylinders(info) {
        const { units } = this.props.values

        return (
            <>
                {Number.isFinite(info.centerToCenter) && (
                    <MeasurementField
                        name="center to center"
                        data={info.centerToCenter}
                        units={units}
                    />
                )}
                <MeasurementField
                    name="radial diff"
                    data={info.radialDiff}
                    units={units}
                />
                {Number.isFinite(info.angle) && (
                    <MeasurementField name="angle" data={info.angle} />
                )}
            </>
        )
    }

    renderPointToPoint = info => {
        const { units } = this.props.values

        return (
            <>
                <MeasurementField
                    name="distance"
                    data={info.distance}
                    units={units}
                />
                <MeasurementField name="x" data={info.x} units={units} />
                <MeasurementField name="y" data={info.y} units={units} />
                <MeasurementField name="z" data={info.z} units={units} />
            </>
        )
    }

    renderMeasure = (info, idx) => {
        let measurement = null

        if (info.type === CYLINDER_INFO) {
            measurement = this.renderCylinder(info)
        } else if (info.type === TWO_PLANES_INFO) {
            measurement = this.renderPlanes(info)
        } else if (info.type === SPHERE_INFO) {
            measurement = this.renderSphere(info)
        } else if (info.type === TORUS_INFO) {
            measurement = this.renderTorus(info)
        } else if (info.type === CONE_INFO) {
            measurement = this.renderCone(info)
        } else if (info.type === POINT_TO_POINT_INFO) {
            measurement = this.renderPointToPoint(info)
        } else if (info.type === TWO_CYLINDERS_INFO) {
            measurement = this.renderCylinders(info)
        }

        if (!measurement) {
            return null
        }
        return (
            <div key={info.id || idx} className={styles.measure}>
                {measurement}
            </div>
        )
    }

    onMeasureChange = evt => {
        const { value } = evt.target

        const typedUnits = value === MM ? MM : IN

        if (this.props.setMeasureUnits) {
            this.props.setMeasureUnits(typedUnits)
        }
    }

    render() {
        const { measures, units } = this.props.values

        return (
            <div className={styles.inner}>
                <div className={styles.header}>
                    <Box justifyContent="between">
                        <h1 className={styles.title}>Measurement</h1>
                        <Radio.Group
                            style={{ display: 'flex', flexDirection: 'row' }}
                            onChange={this.onMeasureChange}
                            value={units}
                            size="small"
                        >
                            <Radio value={MM} className={styles.radioText}>
                                {MM}
                            </Radio>
                            <Radio value={IN} className={styles.radioText}>
                                {IN}
                            </Radio>
                        </Radio.Group>
                    </Box>
                </div>
                {measures.length > 0 ? (
                    measures.map(this.renderMeasure)
                ) : (
                    <div className={styles.measure}>--</div>
                )}
            </div>
        )
    }
}

const MeasurePanel = props => {
    const { panel, values, onSelectMeasurementUnits } = props

    const toolVisibility = useSelector(getToolVisibility)

    if (!toolVisibility?.measurePanel) {
        return null
    }

    const getStyle = () => {
        const opacity = 1

        let top = 15
        let left = 15

        if (panel.position) {
            top = panel.position.y
            left = panel.position.x
        }

        return {
            top,
            left,
            opacity,
        }
    }

    const setMeasureUnits = units => {
        if (onSelectMeasurementUnits) {
            onSelectMeasurementUnits(units)
        }
    }

    return (
        <div className={styles.wrapper} style={getStyle()}>
            <PanelInner setMeasureUnits={setMeasureUnits} values={values} />
        </div>
    )
}

export default MeasurePanel
